import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../utils/api';

const STATUS = {
  upcoming: { label: 'Upcoming', color: 'bg-amber-100 text-amber-700' },
  live:     { label: 'Live',     color: 'bg-red-100 text-red-600'     },
  ended:    { label: 'Ended',    color: 'bg-gray-100 text-gray-500'   },
  cancelled:{ label: 'Cancelled',color: 'bg-slate-100 text-slate-500' },
};

const toLocalDatetimeInput = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ── Field — outside, receives value+onChange as props ──────
const Field = ({ label, type = 'text', placeholder, value, onChange }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                 bg-surface-container-low text-sm outline-none
                 focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
    />
  </div>
);

// ── Create Auction Form ────────────────────────────────────
function CreateAuctionForm({ animals, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    animal_id:     '',
    title:         '',
    description:   '',
    start_price:   '',
    min_increment: '500',
    start_time:    '',
    end_time:      '',
    discount_type: '', discount_value: '', 
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const selectedAnimal = animals.find(a => String(a.id) === String(form.animal_id));
  const basePrice = selectedAnimal ? Number(selectedAnimal.price_inr) : null;
  const isBelowBase = basePrice && Number(form.start_price) > 0 && Number(form.start_price) < basePrice;

  const handleSubmit = async () => {
    if (!form.animal_id || !form.title || !form.start_price || !form.start_time || !form.end_time) {
      setError('Please fill all required fields.');
      return;
    }
    if (new Date(form.end_time) <= new Date(form.start_time)) {
      setError('End time must be after start time.');
      return;
    }
    setSaving(true); setError('');
    try {
      await api.post('/api/farmer/auctions', form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create auction.');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-outline-variant/10 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-black text-on-surface">Create New Auction</h3>
          <p className="text-xs text-secondary mt-0.5">List one of your animals for live bidding</p>
        </div>
        <button onClick={onCancel}
          className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center
                     hover:bg-surface-container-high transition-all">
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>

      {/* Animal selector */}
      <div className="space-y-1.5">
        <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">
          Select Animal *
        </label>
        <select
          value={form.animal_id}
          onChange={set('animal_id')}
          className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                     bg-surface-container-low text-sm outline-none
                     focus:ring-2 focus:ring-primary/20 focus:border-primary/40 transition-all"
        >
          <option value="">— Choose an animal —</option>
          {animals.map(a => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.breed} · {a.weight_kg}kg · ₹{Number(a.price_inr).toLocaleString('en-IN')})
            </option>
          ))}
        </select>
        {animals.length === 0 && (
          <p className="text-xs text-red-500">No active animals found. Add animals first.</p>
        )}
      </div>

      {/* ← now passing value and onChange directly */}
      <Field label="Auction Title *"     value={form.title}         onChange={set('title')}         placeholder="e.g. Premium Boer Buck Auction" />
      <Field label="Description"         value={form.description}   onChange={set('description')}   placeholder="Details about this auction..." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Price (₹) *" value={form.start_price}   onChange={set('start_price')}   type="number" placeholder="5000" />
        <Field label="Min Increment (₹)" value={form.min_increment} onChange={set('min_increment')} type="number" placeholder="500"  />
      </div>

      {/* Discount block — shown when price is below base */}
      {isBelowBase && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-bold text-amber-700">
            Price is below base (₹{basePrice.toLocaleString('en-IN')}). Specify discount:
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">
                Discount Type *
              </label>
              <select value={form.discount_type} onChange={set('discount_type')}
                className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30
                          bg-surface-container-low text-sm outline-none">
                <option value="">Select</option>
                <option value="fixed">Fixed Amount (₹)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            <Field label="Discount Value *" type="number"
              value={form.discount_value} onChange={set('discount_value')}
              placeholder={form.discount_type === 'percent' ? '10' : '500'} />
          </div>
          {form.discount_type && form.discount_value && (
            <p className="text-xs text-amber-700 font-semibold">
              Effective price: ₹{(
                form.discount_type === 'percent'
                  ? basePrice * (1 - Number(form.discount_value) / 100)
                  : basePrice - Number(form.discount_value)
              ).toLocaleString('en-IN')}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Time *" value={form.start_time} onChange={set('start_time')} type="datetime-local" />
        <Field label="End Time *"   value={form.end_time}   onChange={set('end_time')}   type="datetime-local" />
      </div>

      <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-start gap-2">
        <span className="material-symbols-outlined text-amber-500 text-[18px] flex-shrink-0 mt-0.5">info</span>
        <p className="text-xs text-amber-700">
          Auction status will be set to <strong>upcoming</strong> and automatically go <strong>live</strong> at start time.
          The animal will be marked as sold when auction ends with a winning bid.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">error</span>
          {error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={handleSubmit} disabled={saving || animals.length === 0}
          className="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm
                     hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          {saving
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</>
            : <><span className="material-symbols-outlined text-[18px]">gavel</span> Create Auction</>
          }
        </button>
        <button onClick={onCancel}
          className="px-6 py-3 rounded-xl border border-outline-variant text-secondary
                     font-semibold text-sm hover:bg-surface-container transition-all">
          Cancel
        </button>
      </div>
    </div>
  );
}

function EditAuctionForm({ auction, animals, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    title:         auction.title || '',
    description:   auction.description || '',
    start_price:   auction.start_price || '',
    min_increment: auction.min_increment || '500',
    start_time:    toLocalDatetimeInput(auction.start_time),
    end_time:      toLocalDatetimeInput(auction.end_time),
    discount_type:  auction.discount_type || '',
    discount_value: auction.discount_value || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const basePrice   = Number(auction.base_price);
  const isBelowBase = basePrice && Number(form.start_price) < basePrice;

  const handleSubmit = async () => {
    if (!form.title || !form.start_price || !form.start_time || !form.end_time) {
      setError('Please fill all required fields.'); return;
    }
    if (new Date(form.end_time) <= new Date(form.start_time)) {
      setError('End time must be after start time.'); return;
    }
    setSaving(true); setError('');
    try {
      await api.patch(`/api/farmer/auctions/${auction.id}`, form);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update auction.');
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary/20 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-black text-on-surface">Edit Auction</h3>
          <p className="text-xs text-secondary mt-0.5">Changes will require re-approval from admin</p>
        </div>
        <button onClick={onCancel}
          className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center hover:bg-surface-container-high transition-all">
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>

      <Field label="Auction Title *"   value={form.title}         onChange={set('title')}         placeholder="e.g. Premium Boer Buck Auction" />
      <Field label="Description"       value={form.description}   onChange={set('description')}   placeholder="Details about this auction..." />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Price (₹) *" value={form.start_price}   onChange={set('start_price')}   type="number" placeholder="5000" />
        <Field label="Min Increment (₹)" value={form.min_increment} onChange={set('min_increment')} type="number" placeholder="500" />
      </div>

      {isBelowBase && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-bold text-amber-700">
            Price is below base (₹{basePrice.toLocaleString('en-IN')}). Specify discount:
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold text-secondary uppercase tracking-wider">Discount Type *</label>
              <select value={form.discount_type} onChange={set('discount_type')}
                className="w-full px-4 py-2.5 rounded-xl border border-outline-variant/30 bg-surface-container-low text-sm outline-none">
                <option value="">Select</option>
                <option value="fixed">Fixed Amount (₹)</option>
                <option value="percent">Percentage (%)</option>
              </select>
            </div>
            <Field label="Discount Value *" type="number"
              value={form.discount_value} onChange={set('discount_value')}
              placeholder={form.discount_type === 'percent' ? '10' : '500'} />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Start Time *" value={form.start_time} onChange={set('start_time')} type="datetime-local" />
        <Field label="End Time *"   value={form.end_time}   onChange={set('end_time')}   type="datetime-local" />
      </div>

      <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 flex items-start gap-2">
        <span className="material-symbols-outlined text-blue-500 text-[18px] flex-shrink-0 mt-0.5">info</span>
        <p className="text-xs text-blue-700">Saving will reset approval status to <strong>pending</strong> and notify admin for re-review.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">error</span>{error}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <button onClick={handleSubmit} disabled={saving}
          className="flex-1 py-3 rounded-xl bg-primary text-white font-bold text-sm hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
          {saving
            ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
            : <><span className="material-symbols-outlined text-[18px]">save</span> Save & Re-submit</>
          }
        </button>
        <button onClick={onCancel}
          className="px-6 py-3 rounded-xl border border-outline-variant text-secondary font-semibold text-sm hover:bg-surface-container transition-all">
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Auction Card ───────────────────────────────────────────
function AuctionCard({ auction, onEdit, onDelete }) {
  const st = STATUS[auction.status] || STATUS.ended;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-outline-variant/10 overflow-hidden
                    hover:border-primary/20 transition-all">
      <div className="flex flex-col sm:flex-row items-start gap-4 p-4">
        <div className="w-16 h-16 rounded-xl overflow-hidden bg-surface-container flex-shrink-0">
          {auction.image_url
            ? <img src={auction.image_url} alt={auction.animal_name}
                   className="w-full h-full object-cover" />
            : <div className="w-full h-full flex items-center justify-center">
                <span className="material-symbols-outlined text-outline text-[24px]">pets</span>
              </div>
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="font-bold text-on-surface">{auction.title || auction.animal_name}</p>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st.color}`}>
              {st.label}
            </span>
          </div>
          <p className="text-[11px] text-secondary">
            {auction.animal_name} · {auction.breed} · {auction.weight_kg}kg
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {[
              { label: 'Start Price',   val: `₹${Number(auction.start_price).toLocaleString('en-IN')}` },
              { label: 'Current Bid',   val: auction.current_bid ? `₹${Number(auction.current_bid).toLocaleString('en-IN')}` : '—' },
              { label: 'Total Bids',    val: auction.total_bids || 0 },
              { label: 'Min Increment', val: `₹${Number(auction.min_increment).toLocaleString('en-IN')}` },
            ].map(({ label, val }) => (
              <div key={label} className="bg-surface-container-low rounded-xl p-2 text-center">
                <p className="text-[9px] font-bold uppercase text-secondary">{label}</p>
                <p className="text-xs font-black text-on-surface mt-0.5">{val}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 mt-3 text-[11px] text-secondary">
            <span className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[13px]">schedule</span>
              Start: {new Date(auction.start_time).toLocaleString('en-IN')}
            </span>
            <span className="flex items-center gap-1">
              <span className="material-symbols-outlined text-[13px]">timer_off</span>
              End: {new Date(auction.end_time).toLocaleString('en-IN')}
            </span>
          </div>
        </div>

        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-start">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
            auction.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
            auction.approval_status === 'rejected' ? 'bg-red-100 text-red-600' :
                                                    'bg-blue-100 text-blue-600'
          }`}>
            {auction.approval_status === 'approved' ? '✓ Approved' :
            auction.approval_status === 'rejected' ? '✗ Rejected' :
                                                      '⏳ Pending Approval'}
          </span>
          {!(auction.approval_status === 'approved' && auction.status === 'live') && (
            <div className="flex gap-2">
              <button onClick={() => onEdit(auction)}
                className="px-3 py-1.5 rounded-xl border border-primary/30 text-primary text-xs font-bold hover:bg-primary/5 transition-all">
                Edit
              </button>
              <button onClick={() => onDelete(auction.id)}
                className="px-3 py-1.5 rounded-xl border border-red-200 text-red-500 text-xs font-bold hover:bg-red-50 transition-all">
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────
export default function AuctionsPanel() {
  const [auctions, setAuctions] = useState([]);
  const [animals,  setAnimals]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter,   setFilter]   = useState('all');
  const [editingAuction, setEditingAuction] = useState(null);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this auction?')) return;
    try {
      await api.delete(`/api/farmer/auctions/${id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not delete');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [auctRes, animRes] = await Promise.all([
        api.get('/api/farmer/auctions'),
        api.get('/api/farmer/animals?status=active'),
      ]);
      setAuctions(auctRes.data);
      setAnimals(animRes.data);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this auction?')) return;
    try {
      await api.patch(`/api/farmer/auctions/${id}/cancel`, {});
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not cancel auction');
    }
  };

  const filtered = auctions.filter(a => filter === 'all' || a.status === filter);

  const counts = {
    all:      auctions.length,
    upcoming: auctions.filter(a => a.status === 'upcoming').length,
    live:     auctions.filter(a => a.status === 'live').length,
    ended:    auctions.filter(a => a.status === 'ended').length,
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-on-surface">My Auctions</h2>
          <p className="text-sm text-secondary mt-1">Create and manage live bidding auctions.</p>
        </div>
        {!showForm && (
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white
                       font-bold text-sm hover:opacity-90 transition-all shadow-sm">
            <span className="material-symbols-outlined text-[18px]">add</span>
            Create Auction
          </button>
        )}
      </div>

      {showForm && (
        <CreateAuctionForm
          animals={animals}
          onSuccess={() => { setShowForm(false); fetchData(); }}
          onCancel={() => setShowForm(false)}
        />
      )}

      {editingAuction && (
        <EditAuctionForm
          auction={editingAuction}
          animals={animals}
          onSuccess={() => { setEditingAuction(null); fetchData(); }}
          onCancel={() => setEditingAuction(null)}
        />
      )}

      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all',      label: `All (${counts.all})`              },
          { key: 'live',     label: `🔴 Live (${counts.live})`         },
          { key: 'upcoming', label: `🟡 Upcoming (${counts.upcoming})` },
          { key: 'ended',    label: `Ended (${counts.ended})`          },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all ${
              filter === f.key
                ? 'bg-primary text-white'
                : 'bg-white border border-outline-variant/20 text-secondary hover:bg-surface-container'
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl py-16 text-center shadow-sm border border-outline-variant/10">
          <span className="material-symbols-outlined text-5xl text-outline-variant">gavel</span>
          <p className="mt-3 font-bold text-on-surface">No auctions yet</p>
          <p className="text-xs text-secondary mt-1">Click "Create Auction" to start your first live auction.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(a => (
            <AuctionCard key={a.id} auction={a}
              onEdit={(auction) => setEditingAuction(auction)}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}